import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { SecurityClient } from '../../client/security.client';
import { Notification, NotificationType, Session } from '../entity/entities';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(public securityClient: SecurityClient) {}

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (Session.refreshToken && error.error?.Message === 'Invalid token.') {
            return this.handle401Error(request, next);
          } else {
            return this.handleGenericError(request, next, error);
          }
        } else if (error instanceof HttpErrorResponse && error.status == 404) {
          // Callers that own their inline UI (e.g. Insights cards/panels) opt out
          // of the global toaster via this header — honor it for 404 too, not just
          // the generic branch below. Rethrow so the component's own catchError
          // renders an inline empty/not-found state instead.
          if (request.headers.has('X-Skip-Error-Notification')) {
            return throwError(() => error);
          }
          return this.handle404Error(request, next, error);
        }
        else if (!request.headers.has('X-Skip-Error-Notification') && !this.isHandledByPage(error)) {
          return this.handleGenericError(request, next, error);
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private isHandledByPage(error: any): boolean {
    const code = error instanceof HttpErrorResponse && error.status === 412 ? error.error?.error : null;
    return code === 'ZONE_REQUIRED' || code === 'INVALID_TIME_ZONE' || code === 'ADDRESS_CHECK';
  }

  private static messageOf(body: any): string {
    if (body == null) return '';
    if (typeof body === 'string') return body;
    if (typeof body.Message === 'string') return body.Message;
    if (typeof body.message === 'string') return body.message;
    if (typeof body.error === 'string') return body.error;
    try { return JSON.stringify(body); } catch { return String(body); }
  }

  private handleGenericError(request: HttpRequest<any>, next: HttpHandler, error: any) {
    let errorNotification: Notification = new Notification(
      error.statusText == 'OK' ? (error.status==400?"Bad Request":null) : error.statusText,
      ErrorInterceptor.messageOf(error.error),
      NotificationType.Error
    );
    Session.notifications.add(errorNotification.id, errorNotification);

    return throwError(() => error);
  }
  /**
   * Handle 404 - Not Found response from server.
   * @param error
   */
  private handle404Error(request: HttpRequest<any>, next: HttpHandler, error: any) {
    let errorNotification: Notification = new Notification(
      error.statusText,
      typeof error.error == 'string'
        ? error.error
        : `Selected resource could not be found.`,
      NotificationType.Warning,
      10000
    );
    Session.notifications.add(errorNotification.id, errorNotification);

    return throwError(() => error);
  }

  /**
   * Clone paused request(with 401 response) and replace its access token
   * with the one obtained through refresh token action.
   * @param request Stoped request which headers(access token) needs to be updated.
   * @param refreshTokenResponse Data received from refreshToken endpoint.
   */
  private replaceAccessToken(
    request: HttpRequest<any>,
    refreshTokenResponse: any
  ) {
    return request.clone({
      setHeaders: {
        'GratSync-Token': `${refreshTokenResponse.body.access_token}`,
      },
    });
  }

  /**
   * Try to refresh access token on receiving 401 - Unauthorized exception from server.
   * On success - call replaceAccessToken method.
   * On failure - logout with appropriate message.
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.securityClient.refreshToken(Session.refreshToken).pipe(
        switchMap((refreshTokenResponse: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(refreshTokenResponse);
          Session.accessTokenUpdated$.next(true);
          return next.handle(
            this.replaceAccessToken(request, refreshTokenResponse)
          );
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((refreshTokenResponse) => refreshTokenResponse != null),
        take(1),
        switchMap((refreshTokenResponse) => {
          return next.handle(
            this.replaceAccessToken(request, refreshTokenResponse)
          );
        })
      );
    }
  }
}
